<template>
  <div class="card-bonuses" :class="{ 'is-active': props.bonus.status === BonusStatus.ACTIVE }">
    <div class="card-bonuses__body">
      <div class="card-bonuses__header">
        <h4 class="card-bonuses__title">{{ props.bonus.name }}</h4>

        <div class="card-bonuses__msg">
          <template v-if="expiredAtDate">
            <atomic-icon id="history" />

            <div class="card-bonuses__date">
              {{ expiredAtDate }}
            </div>
          </template>

          <span v-if="props.mode === 'bonus' || !props.bonus.usedCount" class="trash-icon">
            <button @click="emit('removeBonus')">
              <atomic-icon id="trash" />
            </button>
          </span>
        </div>
      </div>

      <div v-if="props.mode === 'free-spin' && gameInfo" class="card-bonuses__game">
        <!--        Game name-->

        {{ props.content?.gameLabel }}:
        <nuxt-link :to="localizePath(`/game/${gameInfo.identity}`)" class="card-bonuses__link">
          {{ gameInfo.name }}
        </nuxt-link>
      </div>

      <div class="card-bonuses__conditions" @click="emit('showConditions')">
        {{ props.content?.conditions }}
      </div>

      <div class="card-bonuses__amount">
        <div class="card-bonuses__value">
          <template v-if="props.mode === 'bonus'"> {{ balanceFormat.amount }} {{ balanceFormat.currency }} </template>

          <template v-else-if="props.bonus.usedCount">
            {{ props.bonus.usedCount }} / {{ props.bonus.count }} {{ props.content?.freeSpins.title }}
          </template>

          <template v-else> {{ props.bonus.count }} {{ props.content?.freeSpins.title }} </template>
        </div>
      </div>

      <div class="card-bonuses__info">
        <div v-if="props.mode === 'bonus'" v-html="tooltipContent" />
      </div>

      <!--      Progress bar-->

      <div
        v-if="props.mode === 'bonus'"
        class="card-bonuses__progress"
        :class="{ 'is-riched': isRiched, 'is-greater-zero': greaterZero }"
      >
        <div class="card-bonuses__progress-line" :style="{ '--progress': `${props.bonus.currentWagerPercentage}%` }" />
      </div>

      <div v-else class="card-bonuses__progress" :class="{ 'is-riched': isRiched, 'is-greater-zero': greaterZero }">
        <div class="card-bonuses__progress-line" :style="{ '--progress': `${props.bonus.progress}%` }" />
      </div>

      <!--      Result-->

      <div v-if="isRiched" class="card-bonuses__achive">
        <atomic-icon id="clock" />
        <span>{{ props.content?.cashBonuses?.waitingResult }}</span>
      </div>

      <div class="card-bonuses__bottom">
        <div :class="['card-bonuses__bottom-left', { active: props.bonus.status === BonusStatus.ACTIVE }]">
          {{
            `${props.bonus.status === BonusStatus.ACTIVE ? props.content?.status.active : props.content?.status.notActive}`
          }}
        </div>
      </div>

      <div class="card-bonus__actions">
        <button
          v-if="props.mode === 'bonus' || !props.bonus.usedCount"
          :class="[
            'card-bonuses__activate-btn',
            { 'card-bonuses__activate-btn_active': props.bonus.status !== BonusStatus.ACTIVE },
          ]"
          @click="clickToggle"
        >
          {{ btnTxt }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { IPlayerFreeSpin, IGame, IPlayerBonus } from '@skeleton/core/types';
  import type { IProfileBonuses } from '~/types';
  import { storeToRefs } from 'pinia';

  const BonusStatus = {
    ACTIVE: 2,
  };

  const props = defineProps<{
    bonus: IPlayerBonus | IPlayerFreeSpin;
    content?: IProfileBonuses;
    mode: 'bonus' | 'free-spin';
  }>();

  const { layoutData, defaultLocaleLayoutData } = storeToRefs(useGlobalStore());

  const dayjs = useDayjs();
  const { formatBalance, localizePath, getContent } = useProjectMethods();
  const balanceFormat = computed(() => formatBalance(props.bonus.currency, props.bonus.amount));
  const tooltipContent = computed(() => {
    const bonusWagerText = props.content?.cashBonuses?.casinoWagerLabel.replace(
      '{wager}',
      `${props.bonus.wagerCasino}`
    );
    const sportsbookWagerText = props.content?.cashBonuses?.sportsbookWagerLabel.replace(
      '{wager}',
      `${props.bonus.wagerSportsbook}`
    );
    return `${bonusWagerText}</br>${sportsbookWagerText}`;
  });

  const btnTxt = computed(() => {
    const contentPath = props.bonus.status === BonusStatus.ACTIVE ? 'buttons.disable' : 'buttons.activate';
    return getContent(layoutData.value, defaultLocaleLayoutData.value, contentPath)
  });

  const dateFormat = 'DD.MM.YYYY, HH:mm';
  const expiredAtDate = computed(() => {
    if (props.bonus.status === BonusStatus.ACTIVE) {
      if (props.bonus.wageringExpiredAt) {
        return dayjs(props.bonus.wageringExpiredAt).format(dateFormat);
      }
      return undefined;
    }

    if (props.bonus.activationExpiredAt) {
      return dayjs(props.bonus.activationExpiredAt).format(dateFormat);
    }

    if (props.bonus.wageringExpiredAt) {
      return dayjs(props.bonus.wageringExpiredAt).format(dateFormat);
    }

    return undefined;
  });

  const emit = defineEmits(['switchBonus', 'removeBonus', 'showConditions', 'getGameInfo']);
  const clickToggle = (): void => {
    emit('switchBonus');
  };

  const isRiched = computed(() => {
    if (props.mode === 'bonus') return props.bonus.openedTransactionsCount > 0;
    return props.bonus.progress === 100;
  });

  const greaterZero = computed(() => {
    if (props.mode === 'bonus') return (props.bonus.currentWagerPercentage || 0) > 0;
    return (props.bonus.progress || 0) > 0;
  });

  const gameInfo = ref<IGame | undefined>();
  const getGameData = async (): Promise<void> => {
    const { getGamesInfo } = useCoreGamesApi();
    gameInfo.value = await getGamesInfo(props.bonus.gameId);
    emit('getGameInfo', gameInfo.value);
  };

  onMounted(() => {
    if (props.mode === 'free-spin') getGameData();
  });
</script>

<style src="~/assets/styles/components/card/bonuses.scss" lang="scss" />
